import { AlertConfigurationResponse } from "./AlertConfigurationsService"
import {
   createApiRequest,
   ApiResponse,
   Header,
   getHeaderFromSessionStorage,
} from "@/api"
import { API_URL, Endpoints } from "@/constants/endpoints"
import { HttpMethods } from "@/constants/globals"
import { AlertConfigurationForm } from "@/types/alertConfigurations"
import {
   FacilityAddress,
   FacilityLevelManagementItem,
   Level,
   LevelForm,
   LevelType,
   Sensor,
} from "@/types/facility"

export enum LevelSettingType {
   AUTO_OCCUPIED_ENABLED = "autoOccupiedEnabled",
   AUTO_OCCUPIED_PERIOD = "autoOccupiedPeriod",
   ALARM_FAST_CLEARANCE_ENABLED = "alarmFastClearanceEnabled",
   INMATES_READ_ONLY = "inmatesReadOnly",
   JMS_DATA_PROVIDER = "jmsDataProvider",
   ADD_DOOR_SENSOR_ENABLED = "addDoorSensorsEnabled",
   DYNAMIC_THRESHOLDS_ENABLED = "dynamicThresholdsEnabled",
   OPEN_CELL_SCHEDULING_ENABLED = "openCellSchedulingEnabled",
   MEASUREMENT_SYSTEM = "measurementSystem",
}

export interface LevelSettingResponse {
   key: LevelSettingType
   value: string | number | boolean | null
}

export type LevelAlertConfigurationResponse = AlertConfigurationResponse & {
   source: string | null
}

export interface FacilityLevelResponse {
   id: string
   name: string
   hasCustomConfigurations: boolean
   customIdentifier: string
   type: LevelType
   parents: string[]
   sensors: Sensor[]
   address: FacilityAddress | null
   alertConfigurations: LevelAlertConfigurationResponse[]
   vacant: boolean
   maintenance: boolean
}

export interface LevelVitalsResponse {
   averages: {
      heartRate: number | null
      respiratoryRate: number | null
      totalCountHr: number | null
      totalCountRr: number | null
   }
   distance: number
   heartRate: number
   heartRateStabilityScore: number
   humanPresence: number
   moving: boolean
   respiratoryRate: number
   respiratoryStabilityScore: number
}

export type VitalGraphResolution = "5min"

export type VitalGraphHeaderKey =
   "heart_rate" | "respiratory_rate" | "movement_index" | "human_presence"

export interface LevelVitalsGraphResponse {
   resolution: VitalGraphResolution
   start: string
   end: string
   header: VitalGraphHeaderKey[]
   data: Record<
      string,
      [number | null, number | null, number | null, number | null]
   >
}

export interface VitalsGraphChartProps {
   data: LevelVitalsGraphResponse | null
   loading: boolean
   timezone: string
}

export interface LevelExportRequest {
   level: string
   sensor: string
   startDate: string
   endDate: string
   timezone: string
}

export interface LevelLogsExportRequest {
   level: string
   startDate: string
   endDate: string
   timezone?: string
   alarmsOnly?: boolean
}

export interface LevelLogsExportResponse {
   id: string
   status: string
}

export async function requestCreateFacilityLevel(
   levelForm: Partial<LevelForm>[]
): Promise<ApiResponse<Level[]>> {
   const response = await createApiRequest(
      `${API_URL}${Endpoints.LEVELS}`,
      HttpMethods.POST,
      levelForm,
      getHeaderFromSessionStorage(Header.FACILITY_ID)
   )
   return response
}

export async function requestCreateFacilityLevelAlertConfiguration(
   levelId: string,
   source: string,
   alertConfiguration: Partial<AlertConfigurationForm>
): Promise<ApiResponse<LevelAlertConfigurationResponse>> {
   const response = await createApiRequest(
      `${API_URL}${Endpoints.LEVELS}${levelId}/alert-configurations/`,
      HttpMethods.POST,
      { source, ...alertConfiguration },
      getHeaderFromSessionStorage(Header.FACILITY_ID)
   )
   return response
}

export async function requestUpdateFacilityLevel(
   levelId: string,
   levelForm: Partial<LevelForm | FacilityLevelManagementItem>
): Promise<ApiResponse<Level>> {
   const response = await createApiRequest(
      `${API_URL}${Endpoints.LEVELS}${levelId}/`,
      HttpMethods.PATCH,
      levelForm,
      getHeaderFromSessionStorage(Header.FACILITY_ID)
   )
   return response
}

export async function requestUpdateFacilityLevelAlertConfiguration(
   levelId: string,
   configurationId: string,
   alertConfiguration: Partial<AlertConfigurationForm>
): Promise<ApiResponse<LevelAlertConfigurationResponse>> {
   const response = await createApiRequest(
      `${API_URL}${Endpoints.LEVELS}${levelId}/alert-configurations/${configurationId}/`,
      HttpMethods.PATCH,
      { ...alertConfiguration },
      getHeaderFromSessionStorage(Header.FACILITY_ID)
   )
   return response
}

export async function requestDeleteFacilityLevel(
   levelId: string,
   parentId?: string | null
): Promise<ApiResponse<void>> {
   const response = await createApiRequest(
      `${API_URL}${Endpoints.LEVELS}${levelId}/`,
      HttpMethods.DELETE,
      parentId ? { parentId } : null,
      getHeaderFromSessionStorage(Header.FACILITY_ID)
   )
   return response
}

export async function requestDeleteFacilityLevelAlertConfiguration(
   levelId: string,
   configurationId: string
): Promise<ApiResponse<void>> {
   const response = await createApiRequest(
      `${API_URL}${Endpoints.LEVELS}${levelId}/alert-configurations/${configurationId}/`,
      HttpMethods.DELETE,
      null,
      getHeaderFromSessionStorage(Header.FACILITY_ID)
   )
   return response
}
// Not used yet
export async function requestFacilityLevels(): Promise<
   ApiResponse<FacilityLevelResponse[]>
> {
   const response = await createApiRequest(
      `${API_URL}${Endpoints.LEVELS}`,
      HttpMethods.GET,
      null,
      getHeaderFromSessionStorage(Header.FACILITY_ID)
   )
   return response
}

export async function requestFacilityLevel(
   levelId: string
): Promise<ApiResponse<FacilityLevelResponse>> {
   const response = await createApiRequest(
      `${API_URL}${Endpoints.LEVELS}${levelId}/`,
      HttpMethods.GET,
      null,
      getHeaderFromSessionStorage(Header.FACILITY_ID)
   )
   return response
}

export async function requestLevelVitals(
   levelId: string
): Promise<ApiResponse<LevelVitalsResponse>> {
   const response = await createApiRequest(
      `${API_URL}${Endpoints.LEVELS}${levelId}/vitals/`,
      HttpMethods.GET,
      null,
      getHeaderFromSessionStorage(Header.FACILITY_ID)
   )
   return response
}

export async function requestLevelVitalsGraph(
   levelId: string,
   startDate: string,
   endDate: string,
   timezone: string
): Promise<ApiResponse<LevelVitalsGraphResponse>> {
   const response = await createApiRequest(
      `${API_URL}${Endpoints.LEVELS}${levelId}/vitals/history/?start_date=${startDate}&end_date=${endDate}&timezone=${encodeURIComponent(timezone)}`,
      HttpMethods.GET,
      null,
      getHeaderFromSessionStorage(Header.FACILITY_ID)
   )
   return response
}

export async function requestExportLevelData(
   exportRequest: LevelExportRequest
): Promise<ApiResponse<void>> {
   const response = await createApiRequest(
      `${API_URL}${Endpoints.LEVELS_EXPORT}`,
      HttpMethods.POST,
      exportRequest,
      getHeaderFromSessionStorage(Header.FACILITY_ID)
   )
   return response
}

export async function requestExportLevelLogs(
   exportRequest: LevelLogsExportRequest
): Promise<ApiResponse<LevelLogsExportResponse>> {
   const response = await createApiRequest(
      `${API_URL}${Endpoints.LEVELS}logs/export/`,
      HttpMethods.POST,
      exportRequest,
      getHeaderFromSessionStorage(Header.FACILITY_ID)
   )
   return response
}
